export interface ParsedBackendError {
    status: number | null;
    payload: unknown;
    message: string;
}

const FALLBACK_MESSAGE = 'Die Aktion konnte nicht gespeichert werden.';

/**
 * Sammelt rekursiv alle echten Strings aus einem (moeglicherweise verschachtelten)
 * Fehler-Payload ein. Noetig, weil DRF bei `many=True`-verschachtelten Serializern
 * (z. B. Bereiche mit Optionen) Fehler als Liste von Objekten liefert
 * (`{"fields": [{"label": ["..."], "options": [{"label": ["..."]}]}]}`)  -- eine
 * flache `String(entry)`-Behandlung wuerde daraus "[object Object]" machen.
 */
const collectMessages = (value: unknown): string[] => {
    if (typeof value === 'string') {
        return [value];
    }
    if (Array.isArray(value)) {
        return value.flatMap(collectMessages);
    }
    if (value && typeof value === 'object') {
        return Object.values(value as Record<string, unknown>).flatMap(collectMessages);
    }
    return [];
};

/**
 * Zerlegt die von `customFetcher` geworfene Fehlermeldung
 * (`"Error: <status> <statusText> - <body>"`) in Status, Rohpayload und eine
 * anzeigbare deutsche Meldung.
 */
export const parseBackendError = (error: unknown): ParsedBackendError => {
    if (!(error instanceof Error)) {
        return { status: null, payload: null, message: FALLBACK_MESSAGE };
    }

    const statusMatch = error.message.match(/^Error:\s(\d{3})\s/);
    const status = statusMatch ? Number(statusMatch[1]) : null;

    const marker = ' - ';
    const markerIndex = error.message.indexOf(marker);
    if (markerIndex === -1) {
        return { status, payload: null, message: error.message };
    }

    const rawBody = error.message.slice(markerIndex + marker.length);
    let payload: unknown = null;
    try {
        payload = JSON.parse(rawBody) as unknown;
    } catch (_parseError) {
        return { status, payload: null, message: rawBody || FALLBACK_MESSAGE };
    }

    if (payload && typeof payload === 'object') {
        const record = payload as Record<string, unknown>;
        if (typeof record.detail === 'string') {
            return { status, payload, message: record.detail };
        }
        const fieldMessages = collectMessages(record);
        if (fieldMessages.length > 0) {
            return { status, payload, message: fieldMessages.join(' | ') };
        }
    }

    return { status, payload, message: rawBody || FALLBACK_MESSAGE };
};
